import PropTypes from 'prop-types';
import { Suspense, lazy, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
// hooks
import useSafeTranslation from 'src/hooks/useSafeTranslation';
// utils
import { fBytes } from '../../../utils/universeFormat';
import { downloadFile } from '../../../utils/downloadFile';
// components
import { useSnackbar } from 'notistack';
import Icon from 'src/components/universe/Icon';
import { FileKindTile, breakableName, fileNameOf } from 'src/components/universe/fileKinds';
import { iconButtonSx, secondaryButtonSx } from 'src/theme/universe/buttonSx';
import { UNIVERSE_FONT, accentOf, focusRingSx, tabularSx } from 'src/theme/universe/brand';

// ----------------------------------------------------------------------
// ONE FILE OF A SHARED FOLDER, as one quiet row (29/10/2026, the owner: "troppo
// crowded la pagina, semplifica"): what kind of file, its name — which opens
// it — what it weighs, and Download, which really saves it (utils/downloadFile).
//
// The reader (react-pdf and pdf.js, ~450 KB) is fetched the first time a row
// is pointed at or opened, never with the page.

const loadViewer = () => import('../../../components/DocumentViewer/DocumentViewerModal');
const DocumentViewerModal = lazy(loadViewer);

/** Starts fetching the reader before the click lands; a failure waits for the click. */
const warmViewer = () => {
  loadViewer().catch(() => {});
};

FileCard.propTypes = {
  file: PropTypes.object,
  sx: PropTypes.object
};

export default function FileCard({ file, sx }) {
  const theme = useTheme();
  const { t } = useSafeTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [reading, setReading] = useState(false);
  const [readerMounted, setReaderMounted] = useState(false);
  const [saving, setSaving] = useState(false);
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const url = file?.fileName;
  const name = fileNameOf(url);
  const size = fBytes(file?.size);

  const openReader = () => {
    setReaderMounted(true);
    setReading(true);
  };

  const handleDownload = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await downloadFile(url, name);
    } catch (error) {
      enqueueSnackbar(
        error?.message || t('publicFiles.downloadFailed', { defaultValue: 'The file could not be downloaded.' }),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadLabel = t('publicFiles.downloadOf', {
    defaultValue: 'Download {{name}}',
    name: name || 'this file'
  });

  return (
    <>
      <Box
        onPointerEnter={warmViewer}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
          alignItems: 'center',
          columnGap: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          ...sx
        }}
      >
        <FileKindTile name={name} size={36} />

        <Box
          component="button"
          type="button"
          title={name || undefined}
          onClick={openReader}
          onFocus={warmViewer}
          aria-label={t('publicFiles.openOf', { defaultValue: 'Open {{name}}', name: name || 'this file' })}
          sx={{
            p: 0,
            m: 0,
            border: 0,
            minWidth: 0,
            bgcolor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            borderRadius: '4px',
            fontFamily: UNIVERSE_FONT,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'text.primary',
            // two lines at most, broken after an underscore: on a phone the
            // end of the name (.pdf or .docx) is what tells two files apart
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflowWrap: 'anywhere',
            '&:hover': { color: accentOf(theme), textDecoration: 'underline' },
            '&:focus-visible': focusRingSx(theme)
          }}
        >
          {name ? breakableName(name) : t('publicFiles.unnamed', { defaultValue: 'Unnamed file' })}
        </Box>

        <Typography
          component="span"
          sx={{
            fontFamily: UNIVERSE_FONT,
            fontSize: 13,
            fontWeight: 500,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            ...tabularSx
          }}
        >
          {size || '—'}
        </Typography>

        {/* a word on a wide screen, a mark on a phone */}
        {compact ? (
          <Tooltip arrow title={t('publicFiles.download', { defaultValue: 'Download' })}>
            <span>
              <IconButton
                onClick={handleDownload}
                disabled={saving}
                aria-label={downloadLabel}
                sx={{ ...iconButtonSx(theme, 36), color: accentOf(theme) }}
              >
                {saving ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                ) : (
                  <Icon name="DocumentDownload" size="sm" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            size="small"
            onClick={handleDownload}
            disabled={saving}
            aria-label={downloadLabel}
            startIcon={
              saving ? (
                <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
              ) : (
                <Icon name="DocumentDownload" size={16} />
              )
            }
            sx={secondaryButtonSx(theme, 'small')}
          >
            {saving
              ? t('publicFiles.downloading', { defaultValue: 'Downloading…' })
              : t('publicFiles.download', { defaultValue: 'Download' })}
          </Button>
        )}
      </Box>

      {readerMounted && (
        <Suspense
          fallback={
            <Backdrop open sx={{ zIndex: theme.zIndex.modal }}>
              <CircularProgress sx={{ color: 'common.white' }} />
            </Backdrop>
          }
        >
          <DocumentViewerModal
            open={reading}
            onClose={() => setReading(false)}
            documentUrl={url}
            documentName={name}
          />
        </Suspense>
      )}
    </>
  );
}
